
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { AlertCircle, BarChart3 } from "lucide-react";

interface DistributionData {
  name: string;
  value: number;
  percentual: number;
}

interface AgencyDistributionChartsProps {
  typeDistribution: DistributionData[];
  neighborhoodDistribution: DistributionData[];
  type: 'venda' | 'aluguel';
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-2 rounded shadow-lg border">
        <p className="text-sm">{`${data.name}: ${data.value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`}</p>
        <p className="text-sm text-muted-foreground">{`${data.percentual.toFixed(1)}%`}</p>
      </div>
    );
  }
  return null;
};

const NoDataMessage = ({ title, message, icon: Icon }: { title: string; message: string; icon: any }) => (
  <div className="h-[400px] flex flex-col items-center justify-center text-center p-6">
    <Icon className="h-12 w-12 text-muted-foreground mb-4" />
    <h4 className="text-lg font-medium text-muted-foreground mb-2">{title}</h4>
    <p className="text-sm text-muted-foreground max-w-md">{message}</p>
  </div>
);

const AgencyDistributionCharts = ({ typeDistribution, neighborhoodDistribution, type }: AgencyDistributionChartsProps) => {
  const hasTypeData = typeDistribution && typeDistribution.length > 0;
  const hasNeighborhoodData = neighborhoodDistribution && neighborhoodDistribution.length > 0;
  
  // Calculate dynamic height based on number of items
  const getChartHeight = (itemCount: number) => {
    if (itemCount <= 3) return 250;
    if (itemCount <= 6) return 350;
    if (itemCount <= 10) return 400;
    if (itemCount <= 12) return 450;
    return Math.min(500, 250 + (itemCount * 20)); // Dynamic height for more items
  };

  // Get dynamic title for neighborhoods with tie handling
  const getNeighborhoodTitle = (count: number) => {
    if (count <= 1) return "Bairro de Atuação";
    if (count <= 5) return "Bairros de Atuação";
    if (count === 10) return "Top 10 Bairros";
    if (count > 10) {
      // Check if we have ties by examining if there are more than 10 items
      return `Top 10+ Bairros (${count} bairros)`;
    }
    return `Top ${count} Bairros`;
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">
          Distribuição por Tipo
          {hasTypeData && (
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({typeDistribution.length} tipo{typeDistribution.length !== 1 ? 's' : ''})
            </span>
          )}
        </h3>
        
        {!hasTypeData ? (
          <NoDataMessage 
            title="Nenhum dado disponível"
            message="Esta imobiliária não possui dados de tipos de imóveis para análise."
            icon={AlertCircle}
          />
        ) : (
          <div style={{ height: `${getChartHeight(typeDistribution.length)}px`, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={typeDistribution}
                margin={{ 
                  top: 5, 
                  right: 30, 
                  left: typeDistribution.length === 1 ? 80 : 100, 
                  bottom: 5 
                }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" tickFormatter={(value) => `${value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`} />
                <YAxis dataKey="name" type="category" width={typeDistribution.length === 1 ? 70 : 90} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" fill="#0088FE" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">
          {hasNeighborhoodData ? getNeighborhoodTitle(neighborhoodDistribution.length) : "Bairros de Atuação"}
          {hasNeighborhoodData && (
            <span className="text-sm font-normal text-muted-foreground ml-2">
              ({neighborhoodDistribution.length} bairro{neighborhoodDistribution.length !== 1 ? 's' : ''})
            </span>
          )}
        </h3>
        
        {!hasNeighborhoodData ? (
          <NoDataMessage 
            title="Nenhum dado disponível"
            message="Esta imobiliária não possui dados de bairros para análise."
            icon={AlertCircle}
          />
        ) : (
          <div style={{ height: `${getChartHeight(neighborhoodDistribution.length)}px`, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={neighborhoodDistribution}
                margin={{ 
                  top: 5, 
                  right: 30, 
                  left: neighborhoodDistribution.length === 1 ? 80 : 100, 
                  bottom: 5 
                }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" tickFormatter={(value) => `${value.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`} />
                <YAxis dataKey="name" type="category" width={neighborhoodDistribution.length === 1 ? 70 : 90} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" fill="#00C49F" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AgencyDistributionCharts;
